import { CalendarPlus, CheckCircle, GlobeV2, PaperPlaneTilt, XCircle } from "@piiqtechnologies/svg-icons";
import { PeekBox, PeekLoader, PeekStack, PeekTypography, useTheme } from "@piiqtechnologies/ui-components";
import { formatDateFromUTC, useScanRequests } from "@piiqtechnologies/ui-lib";
import { isBefore } from "date-fns";
import React, { useEffect, useMemo } from "react";
import { useIntl } from "react-intl";
import messages from "./messages.i18n";
const CentralizeContentFrame = ({ children }) => {
    return (React.createElement(PeekBox, { sx: {
            width: "100%",
            minWidth: "300px",
            height: "100%",
            minHeight: "200px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
        } }, children));
};
const HistoryBoldText = (text) => {
    return React.createElement(PeekTypography, { fontWeight: "fontWeightBold" }, text);
};
const HistoryItemStructure = ({ date, Icon, text }) => {
    const theme = useTheme();
    return (React.createElement(PeekStack, { direction: "row", spacing: theme.spacing(2) },
        React.createElement(PeekTypography, { variant: "body1", color: theme.palette.grey[300] }, formatDateFromUTC(new Date(date), "MM/dd/yy")),
        React.createElement(PeekBox, { sx: { transform: "translateY(-2px)" } }, Icon),
        text));
};
var HistoryEventType;
(function (HistoryEventType) {
    HistoryEventType["REQUESTED_AT"] = "requestedAt";
    HistoryEventType["CANCELED_AT"] = "canceledAt";
    HistoryEventType["SKIPPED_AT"] = "skippedAt";
    HistoryEventType["SCHEDULED_AT"] = "scheduledAt";
    HistoryEventType["COMPLETED_AT"] = "scannedAt";
    HistoryEventType["UPLOADED_AT"] = "uploadedAt";
})(HistoryEventType || (HistoryEventType = {}));
export const ScanRequestHistory = ({ spaceId }) => {
    const theme = useTheme();
    const intl = useIntl();
    const [getScanRequests, scanRequests, isLoading, error] = useScanRequests();
    useEffect(() => {
        getScanRequests({ "space._id": spaceId });
    }, [spaceId]);
    const eventsOrderedByDate = useMemo(() => {
        var _a;
        const events = [];
        if (!((_a = scanRequests === null || scanRequests === void 0 ? void 0 : scanRequests.data) === null || _a === void 0 ? void 0 : _a.length))
            return events;
        scanRequests.data.forEach((scanRequest) => {
            var _a;
            // Every event has a submission date
            events.push({ type: HistoryEventType.REQUESTED_AT, value: (_a = scanRequest.requestedAt) !== null && _a !== void 0 ? _a : scanRequest.createdAt });
            if (scanRequest.canceledAt) {
                events.push({ type: HistoryEventType.CANCELED_AT, value: scanRequest.canceledAt });
            }
            if (scanRequest.skippedAt) {
                events.push({ type: HistoryEventType.SKIPPED_AT, value: scanRequest.skippedAt });
            }
            if (scanRequest.scheduledAt) {
                events.push({ type: HistoryEventType.SCHEDULED_AT, value: scanRequest.scheduledAt });
            }
            if (scanRequest.scannedAt) {
                events.push({ type: HistoryEventType.COMPLETED_AT, value: scanRequest.scannedAt });
            }
            if (scanRequest.uploadedAt) {
                events.push({ type: HistoryEventType.UPLOADED_AT, value: scanRequest.uploadedAt });
            }
        });
        events.sort((a, b) => (isBefore(new Date(a.value), new Date(b.value)) ? 1 : -1));
        return events;
    }, [scanRequests]);
    /**
     * This function builds the UI for each event in the history.
     * @param event object representing the event
     * @returns a JSX.Element representing the event history item
     */
    const getEventHistoryItem = (event) => {
        var _a, _b, _c, _d;
        const eventScanRequest = scanRequests.data.find((scanRequest) => scanRequest[event.type] === event.value);
        switch (event.type) {
            case HistoryEventType.REQUESTED_AT:
                return (React.createElement(HistoryItemStructure, { date: event.value, Icon: React.createElement(PeekTypography, { variant: "h2" },
                        React.createElement(PaperPlaneTilt, null)), text: React.createElement(PeekTypography, { display: "ruby" }, intl.formatMessage(messages.requestedAtMessage, {
                        requesterName: (_b = (_a = eventScanRequest === null || eventScanRequest === void 0 ? void 0 : eventScanRequest.requestedBy) === null || _a === void 0 ? void 0 : _a.name) !== null && _b !== void 0 ? _b : "Peek Team",
                        bold: (text) => HistoryBoldText(text),
                    })) }));
            case HistoryEventType.CANCELED_AT:
                return (React.createElement(HistoryItemStructure, { date: event.value, Icon: React.createElement(PeekTypography, { variant: "h2", color: theme.palette.error.main },
                        React.createElement(XCircle, null)), text: React.createElement(PeekTypography, { display: "ruby" }, intl.formatMessage(messages.canceledAtMessage, {
                        canceledByName: (_c = eventScanRequest.canceledBy) === null || _c === void 0 ? void 0 : _c.name,
                        bold: (text) => HistoryBoldText(text),
                        reason: eventScanRequest.reason
                            ? `${intl.formatMessage(messages.becauseText)} ${eventScanRequest.reason}`
                            : eventScanRequest.comment
                                ? `${intl.formatMessage(messages.becauseText)} ${eventScanRequest.comment}`
                                : "",
                    })) }));
            case HistoryEventType.SKIPPED_AT:
                return (React.createElement(HistoryItemStructure, { date: event.value, Icon: React.createElement(PeekTypography, { variant: "h2", color: theme.palette.orange[400] },
                        React.createElement(XCircle, null)), text: React.createElement(PeekTypography, { display: "ruby" }, intl.formatMessage(messages.skippedAtMessage, {
                        skippedByName: (_d = eventScanRequest.skippedBy) === null || _d === void 0 ? void 0 : _d.name,
                        bold: (text) => HistoryBoldText(text),
                        reason: eventScanRequest.reason
                            ? `${intl.formatMessage(messages.becauseText)} ${eventScanRequest.reason}`
                            : eventScanRequest.comment
                                ? `${intl.formatMessage(messages.becauseText)} ${eventScanRequest.comment}`
                                : "",
                    })) }));
            case HistoryEventType.SCHEDULED_AT:
                return (React.createElement(HistoryItemStructure, { date: event.value, Icon: React.createElement(PeekTypography, { variant: "h2" },
                        React.createElement(CalendarPlus, null)), text: React.createElement(PeekTypography, { display: "ruby" }, intl.formatMessage(messages.scheduledAtMessage, {
                        bold: (text) => HistoryBoldText(text),
                        date: formatDateFromUTC(new Date(eventScanRequest.scheduledFor), "MM/dd/yy"),
                    })) }));
            case HistoryEventType.COMPLETED_AT:
                return (React.createElement(HistoryItemStructure, { date: event.value, Icon: React.createElement(PeekTypography, { variant: "h3", color: theme.palette.success.main },
                        React.createElement(CheckCircle, null)), text: React.createElement(PeekTypography, { display: "ruby" }, intl.formatMessage(messages.completedAtMessage, {
                        bold: (text) => HistoryBoldText(text),
                    })) }));
            case HistoryEventType.UPLOADED_AT:
                return (React.createElement(HistoryItemStructure, { date: event.value, Icon: React.createElement(PeekTypography, { variant: "h2", color: theme.palette.purple[400] },
                        React.createElement(GlobeV2, null)), text: React.createElement(PeekTypography, { display: "ruby" }, intl.formatMessage(messages.uploadedAtMessage, {
                        bold: (text) => HistoryBoldText(text),
                        date: formatDateFromUTC(new Date(event.value), "MM/dd/yy"),
                    })) }));
            default:
                return null;
        }
    };
    /**
     * LOADING STATE
     */
    if (isLoading) {
        return (React.createElement(CentralizeContentFrame, null,
            React.createElement(PeekTypography, { variant: "body1" },
                React.createElement(PeekLoader, null))));
    }
    /**
     * NO DATA STATE
     */
    if (!(eventsOrderedByDate === null || eventsOrderedByDate === void 0 ? void 0 : eventsOrderedByDate.length)) {
        return (React.createElement(CentralizeContentFrame, null,
            React.createElement(PeekTypography, { variant: "body1", color: "grey.300" }, intl.formatMessage(messages.noHistoryAvailable))));
    }
    /**
     * EVENTS HISTORY
     */
    return (React.createElement(React.Fragment, null, !!(eventsOrderedByDate === null || eventsOrderedByDate === void 0 ? void 0 : eventsOrderedByDate.length) && (React.createElement(PeekStack, { direction: "column", spacing: theme.spacing(1) }, eventsOrderedByDate.map((event, index) => (React.createElement(PeekStack, { key: index, direction: "column", spacing: theme.spacing(1) },
        getEventHistoryItem(event),
        index !== eventsOrderedByDate.length - 1 && (React.createElement(PeekBox, { sx: {
                height: "30px",
                width: "1px",
                borderLeft: `1px dashed ${theme.palette.grey[300]}`,
                marginTop: "0 !important",
                transform: "translateX(79px)",
            } })))))))));
};
export default ScanRequestHistory;
